import {
  Scenarios_ScenarioFragment,
  ScenarioTimeline_OrderFragment,
  ShipmentStatus,
} from '../graphql/types';
import { ClientId } from '../lib/clientId';
import { StrictPick } from '../lib/generics';
import {
  CargoAllocation,
  CargoEntry,
  CoaEntry,
  Entry,
  isTCOrderEntry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from './entries';

export type ScenarioPatch<T> = {
  id: number;
} & {
  type: 'create' | 'update';
  patch: Partial<T>;
  // | { type: 'delete' }
};

export type ScenarioOrderPatch = ScenarioPatch<
  Pick<
    ScenarioTimeline_OrderFragment,
    'outgoingVoyageCharterRelation' | 'orderPortCallRelationsByOrderId'
  > & {
    shipmentId: ClientId | null;
  }
>;

export type PatchShipment = StrictPick<ScenarioShipment, 'voyageAttributeId'>;
export type PatchShipmentMap = Map<ClientId, PatchShipment>;

export type ScenarioShipment = {
  clientId: ClientId;
  name: string | null;
  remarks: string | null;
  id: number | null;
  externalShipmentId: string | null;
  externalShipmentStatus: ShipmentStatus | null;
  voyageAttributeId: number | null;
};

export const SCENARIO_DATA_SCHEMA_VERSION = 3 as const;
export const LATEST_MIGRATION_VERSION = 4 as const;

export enum EntryState {
  /** Entry is updated or created in this scenario */
  UPDATED = 'U',
  /** Entry is deleted in this scenario and should remain deleted after sync or commit */
  DELETED = 'D',
}

/**
 * Represents a scenario which may or may not be committed.
 * NOTE! Changes to this structure with extreme care. The structure gets serialized and saved into database.
 * If you update this interface, be sure that the SerializedScenario interface in backend is also updated (so integration scenarios are generated correctly).
 * If the schema is updated, change the dataSchemaVersion so that any older versions of data will not be loaded
 * and coordinate the upgrade with clients if possible so that they don't lose important work.
 */
export type InternalUserSchedule = Pick<
  Scenarios_ScenarioFragment,
  | 'organizationId'
  | 'name'
  | 'description'
  | 'dataSchemaVersion'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'updatedBy'
  | 'shared'
  | 'deletedAt'
> & {
  type: 'user';
  id: number | null;
  clientId: ClientId;
  initials?: string;
  dataSchemaVersion: typeof SCENARIO_DATA_SCHEMA_VERSION;
  migrationVersion: number;
  /** Refers to the latest commit timestamp that this scenario is synced with. Can be used to warn user about potential overwrites on commit. */
  syncedWithMasterAt?: number;
  /** Timestamp of the last known commit according to this scenario. Can be used to protect against e.g. websocket update failures.*/
  masterCommitAt?: number | null;
  isReadOnly: boolean;
  /** Integration scenarios can be marked as "master" scenario. It is always considered to be in sync. undefined interpreted as false. */
  isIntegrationMaster?: boolean;
  integrationData?: {
    externalShipmentIdByClientId?: Record<string, string>;
    orderIdsByShipmentClientId?: Record<string, number[]>;
  };

  /** Timestamp for integration scenario to reflect user local time */
  importedAt?: number;

  /** Scenario setting to override org config for auto update behavior. Default/undefined will respect the org setting. */
  autoUpdateFromMaster?: 'always' | 'never' | 'default';
  changedAt?: number;

  // TODO split `schedule` in scenario into 3 so each array has only entries of a single type. instead of arrays could also be maps keyed by ClientId https://github.com/Seaber-io/frontend/pull/626#discussion_r705253661
  /** All TC assignments */
  schedule: (TCOrderEntry | SingleEntry | OffHireEntry)[];
  cargoAllocations: CargoAllocation[];

  /** All Spot assignments */
  spotAssignments: SpotEntry[];
  /** All coa assignments */
  coaAssignments: CoaEntry[];

  /** All port calls related to any type of timeline */
  portCalls: PortCallEntry[];
  patchOrders: Map<number, ScenarioOrderPatch>;
  patchShipments: PatchShipmentMap;
  entryStates: Map<ClientId, EntryState>; // These mark an actual record vs. last known committed state and should be used for display purposes on the timeline
  voyageCharterByOrderId: Map<number, number | string | null>;
};

/** Mapping of each order id present in the scenario into the assignment entry */
export const getOrderAssignments = (
  scenario: ScenarioSchedule,
): Map<number, CargoEntry> =>
  new Map(
    [
      ...scenario.schedule.filter(isTCOrderEntry),
      ...scenario.coaAssignments,
      ...scenario.spotAssignments,
    ].map((e) => [e.orderId, e]),
  );

export const getAssignedOrderIds = (
  scenario: ScenarioSchedule,
  includedVesselIds: Set<number>,
): Set<number> =>
  new Set(
    [
      ...scenario.schedule
        .filter(isTCOrderEntry)
        .filter(
          (e) => e.vesselId !== null && includedVesselIds.has(e.vesselId),
        ),
      ...scenario.coaAssignments,
      ...scenario.spotAssignments,
    ].map((assignment) => assignment.orderId),
  );

export const getAllScenarioEntries = (scenario: ScenarioSchedule): Entry[] => [
  ...scenario.schedule,
  ...scenario.coaAssignments,
  ...scenario.spotAssignments,
  ...scenario.portCalls,
];

export type InternalSuggestionSchedule = Omit<InternalUserSchedule, 'type'> & {
  type: 'suggestion';
  baseScenarioId: ClientId;
};

export type InternalMasterSchedule = Omit<
  InternalUserSchedule,
  | 'type'
  | 'id'
  | 'isReadOnly'
  | 'entryStates'
  | 'syncedWithMasterAt'
  | 'migrationVersion'
> & {
  type: 'master';
  isReadOnly: true;
};

export type InternalBackupSchedule = Omit<InternalMasterSchedule, 'type'> & {
  type: 'backup';
  backedUpAt: number;
};

export type InternalInitialSchedule = Omit<InternalMasterSchedule, 'type'> & {
  type: 'initial';
};

export type UserSchedule = InternalUserSchedule;
export type SuggestionSchedule = InternalSuggestionSchedule;
export type MasterSchedule = InternalMasterSchedule;
export type BackupSchedule = InternalBackupSchedule;
export type InitialSchedule = InternalInitialSchedule;

export type ScenarioSchedule =
  | UserSchedule
  | SuggestionSchedule
  | MasterSchedule
  | BackupSchedule
  | InitialSchedule;

export function isEntryStateDeleted(
  scenario: ScenarioSchedule,
  entryClientId: ClientId,
): boolean {
  if ('entryStates' in scenario) {
    return scenario.entryStates.get(entryClientId) === EntryState.DELETED;
  } else {
    return false;
  }
}

export function isEntryStateUpdated(
  scenario: ScenarioSchedule,
  entryClientId: ClientId,
): boolean {
  if ('entryStates' in scenario) {
    return scenario.entryStates.get(entryClientId) === EntryState.UPDATED;
  } else {
    return false;
  }
}
